import { computed, isRef, ref, unref, watch, type MaybeRef } from "vue"
import Fuse, { type IFuseOptions, type FuseResult } from "fuse.js"

export type SearchEntry = {
  id: string
  uri: string
  title?: string
  name?: string
  programme?: string
  isGroup?: boolean
}

export function useSearch(items: MaybeRef<SearchEntry[]>) {
  const searchQuery = ref<string | undefined>(undefined)
  const fuseResults = ref<FuseResult<SearchEntry>[]>([])
  const selectedId = ref<string | null>(null)

  // Fuse.js setup
  const fuseOptions: IFuseOptions<SearchEntry> = {
    threshold: 0.3,
    keys: [
      { name: "name", weight: 2 },
      { name: "title", weight: 1.5 },
      { name: "programme", weight: 0.5 },
    ],
    includeMatches: true,
    includeScore: true,
  }

  const fuse = new Fuse(unref(items), fuseOptions)

  // Search logic
  const search = (query: string) => {
    fuseResults.value = fuse.search(query)
    selectedId.value = null
  }

  // Callers may pass a ref whose contents arrive asynchronously (the shared
  // search index loads after the projects store resolves). Keep Fuse's
  // collection in sync and re-run any active query once the corpus lands —
  // but only if fuseResults is empty, meaning the search ran before the
  // corpus was ready. If results already exist the query already ran against
  // a full corpus and re-running would double the results.
  if (isRef(items)) {
    watch(items, next => {
      fuse.setCollection(next)
      if (searchQuery.value && fuseResults.value.length === 0) {
        search(searchQuery.value)
      }
    })
  }

  watch(searchQuery, query => {
    if (!query) {
      fuseResults.value = []
      selectedId.value = null
    } else {
      search(query)
    }
  })

  // Helper for highlighting matches
  const getMostRelevantField = (result: FuseResult<SearchEntry>) => {
    if (!result.matches || result.matches.length === 0) {
      return {
        field: "name",
        value: result.item.name || result.item.title || "Unnamed project",
      }
    }

    const match = result.matches[0]
    const field = match.key || "name" // Default to 'name' if no key is found

    switch (field) {
      case "name":
        return {
          field,
          value: result.item.name || "Unnamed student",
        }
      case "title":
        return {
          field,
          value: result.item.title || "Unnamed project",
        }
      case "programme.title":
        return {
          field: "programme",
          value: result.item.programme || "Unnamed programme",
        }
      default:
        return {
          field,
          value: result.item[field as keyof SearchEntry] || "Unknown",
        }
    }
  }

  const resultsSplitByField = computed(() => {
    return fuseResults.value
      .slice(0, 20)
      .reduce((acc: Record<string, FuseResult<SearchEntry>[]>, result) => {
        let field = getMostRelevantField(result).field
        if (!field) return acc
        if (!acc[field]) acc[field] = []
        if (acc[field].length < 8) {
          acc[field].push(result)
        }
        return acc
      }, {})
  })

  return {
    searchQuery,
    fuseResults,
    selectedId,
    resultsSplitByField,
    search,
    getMostRelevantField,
  }
}
